import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react';
import { Policy, Claim, Order, Analytics } from '@/types';
import {
  getCustomerPolicies,
  getCustomerClaims,
  getCustomerOrders,
  getCustomerAnalytics,
} from '@/service/customerServices';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';

interface CustomerContextType {
  policies: Policy[];
  claims: Claim[];
  orders: Order[];
  analytics: Analytics | null;
  isLoading: boolean;
  error: string | null;
  refreshData: () => Promise<void>;
  refreshClaims: () => Promise<void>;
}

const CustomerContext = createContext<CustomerContextType | undefined>(
  undefined
);

interface CustomerProviderProps {
  children: ReactNode;
}

export const CustomerProvider: React.FC<CustomerProviderProps> = ({
  children,
}) => {
  const { isAuthenticated, loginType } = useAuth();
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [claims, setClaims] = useState<Claim[]>([]);
  const [orders, setOrders] = useState<Order[]>([]);
  const [analytics, setAnalytics] = useState<Analytics | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async (isManual = false) => {
    // Guest users (token-based) should not trigger dashboard API calls
    if (loginType === 'token') {
      return;
    }

    setIsLoading(true);
    setError(null);

    const fetchPromise = Promise.all([
      getCustomerPolicies(),
      getCustomerClaims(),
      getCustomerOrders(),
      getCustomerAnalytics(),
    ]);

    if (isManual) {
      toast.promise(fetchPromise, {
        loading: 'Refreshing data...',
        success: 'Data refreshed successfully!',
        error: 'Failed to refresh data. Please try again.',
      });
    }

    try {
      const [pols, clms, ords, anlys] = await fetchPromise;
      setPolicies(pols);
      setClaims(clms);
      setOrders(ords);
      setAnalytics(anlys);
    } catch (err: any) {
      console.error('CustomerContext fetch error:', err);
      setError(err.message || 'Unknown error while fetching customer data');
    } finally {
      setIsLoading(false);
    }
  };

  const refreshClaims = async () => {
    if (loginType === 'token') return;

    try {
      const [clms, pols] = await Promise.all([
        getCustomerClaims(),
        getCustomerPolicies(),
      ]);
      setClaims(clms);
      setPolicies(pols);
    } catch (err: any) {
      console.error('Failed to refresh claims:', err);
    }
  };

  useEffect(() => {
    // Only fetch dashboard data for regular authenticated users
    if (isAuthenticated && loginType === 'regular') {
      fetchData();
    } else {
      setPolicies([]);
      setClaims([]);
      setOrders([]);
      setAnalytics(null);
    }
  }, [isAuthenticated, loginType]);

  return (
    <CustomerContext.Provider
      value={{
        policies,
        claims,
        orders,
        analytics,
        isLoading,
        error,
        refreshData: () => fetchData(true),
        refreshClaims,
      }}
    >
      {children}
    </CustomerContext.Provider>
  );
};


export const useCustomerContext = (): CustomerContextType => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error(
      'useCustomerContext must be used within a CustomerProvider'
    );
  }
  return context;
};
